import React from 'react';
import Modal from '@sasaki-dev/react-modal';
import {Provider, observer} from 'mobx-react';

import IHenshuDataStore from './store/IHenshuDataStore';
import IHenshuPersistLayer from './persist/IHenshuPersistLayer';
import FirebasePersistLayer, { FirebaseConfig } from './persist/firebase';
import HenshuApp, { IHenshuApp } from './HenshuApp';

import './Henshu.scss';


export type Lang = {
    code: string;
    name: string;
};

export type BootstrapComponents = {
    firebaseConfig: FirebaseConfig;
    store: IHenshuDataStore;
    app?: IHenshuApp;
    persist?: IHenshuPersistLayer;
    defaultLang?: string;
};

export function bootstrap({ app, defaultLang, firebaseConfig, persist, store }: BootstrapComponents) {
    const _app = app ? app :  new HenshuApp();
    const _persist = persist ? persist : new FirebasePersistLayer(firebaseConfig, defaultLang);

    store.onSnapshot((snapshot: object) => {
        _persist.save(snapshot);
    });

    _persist.receive((snapshot: object) => {
        store.applySnapshot(snapshot);
    });

    return { app: _app , persist: _persist };
}

type Layers = {
    app: IHenshuApp;
    store: IHenshuDataStore;
    persist: IHenshuPersistLayer;
};

type HenshuProps = {
    firebaseConfig?: FirebaseConfig;
    store?: IHenshuDataStore;
    layers?: Layers,
    defaultLang?: string;
    langs?: Lang[];
};

type HenshuState = {
    seeding: boolean;
};

@observer
class Henshu extends React.Component<HenshuProps, HenshuState> {

    waitForLoad: boolean = false;

    app: IHenshuApp;
    store: IHenshuDataStore;
    persist: IHenshuPersistLayer;

    constructor(props: HenshuProps) {
        super(props);

        if (props.layers) {
            this.app = props.layers.app;
            this.store = props.layers.store;
            this.persist = props.layers.persist;
        } else if (props.firebaseConfig && props.store) {
            const { app, persist } = bootstrap({ 
                firebaseConfig: props.firebaseConfig, 
                store: props.store, 
                defaultLang: props.defaultLang || 'en',
            });

            this.app = app;
            this.store = props.store;
            this.persist = persist;
        } else {
            throw new Error('You must provide Layers or FirebaseConfig + DataStore to Henshu');
        }

        this.state = {
            seeding: false,
        };
    }

    componentDidMount() {
        window.addEventListener('keypress', this.onKeyPress.bind(this));
    }

    componentWillUnmount() {
        window.removeEventListener('keypress', this.onKeyPress.bind(this));
    }

    private async logout() {
        await this.persist.deauthorize();
        this.app.setUser(null);
    }

    private async onKeyPress(e: KeyboardEvent) {
        if (e.key === '~') {
            if (!this.app.isLoggedIn) {
                const user: any = await this.persist.authorize();
                if (user) {
                    this.app.setUser(user)
                    this.app.setEditMode(true);
                }
            } else {
                this.app.setEditMode(!this.app.editMode);
            }
        }
    }

    render() {
        return (
            <Provider app={this.app} persist={this.persist} store={this.store}>
                {this.waitForLoad && this.persist.loading ? (
                    <div className="Henshu__loading">
                        <h1>Loading ...</h1>
                    </div>
                ) : (
                    <>
                        {this.props.children}

                        {this.app.isEditing && (
                            <div className="Henshu__toolbar">
                                {this.app.user.displayName}

                                <select
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => this.persist.setLang(e.target.value)}>
                                    {this.props.langs ? (
                                        this.props.langs.map(lang => <option value={lang.code}>{lang.name}</option>)
                                    ) : (
                                        <option value={'en-us'}>English</option>
                                    )}
                                </select>

                                {process.env.NODE_ENV === 'development' && (
                                    <button onClick={() => this.setState({seeding: true})}>Seed</button>
                                )}

                                <button onClick={() => this.logout()}>Logout</button>

                                {this.state.seeding && (
                                    <Modal className="Henshu__Modal" onClose={() => this.setState({seeding: false})}>
                                        <p>
                                            Are you sure you want to overwrite the '{this.persist.lang}' data with its
                                            seed?
                                        </p>

                                        <div className="Henshu__modal-buttons">
                                            <button onClick={() => this.setState({seeding: false})}>Cancel</button>
                                            <button onClick={() => {
                                                this.store.seed(this.persist);
                                                this.setState({seeding: false})
                                            }}>Overwrite Data
                                            </button>
                                        </div>
                                    </Modal>
                                )}
                            </div>
                        )}
                    </>
                )}
            </Provider>
        );
    }

}

export default Henshu;
